import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, Filter, Package, TriangleAlert } from 'lucide-react'
import { useInventoryItemPage } from '@/hooks/inventory'
import { Loading } from '@/components/Loading'
import { InlineError } from '@/components/ErrorDisplay'
import { EmptyData } from '@/components/EmptyState'
import { getApiErrorMessage } from '@/api/client'

type StatusFilter = 'all' | 'ok' | 'low' | 'out'

export default function InventoryList() {
  const [searchKeyword, setSearchKeyword] = useState('')
  const [appliedKeyword, setAppliedKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [page, setPage] = useState(0)

  const { data, isLoading, error } = useInventoryItemPage({
    keyword: appliedKeyword || undefined,
    lowStockOnly: statusFilter === 'low' || statusFilter === 'out' ? true : undefined,
    page,
    size: 20,
  })

  const filteredItems =
    data?.content.filter((item) => {
      if (statusFilter === 'out') return item.availableQty === 0
      if (statusFilter === 'low') return item.availableQty > 0 && item.availableQty < item.safetyStockQty
      if (statusFilter === 'ok') return item.availableQty >= item.safetyStockQty
      return true
    }) ?? []

  const stats = {
    totalTypes: data?.totalElements ?? 0,
    totalStock: filteredItems.reduce((sum, item) => sum + item.onHandQty, 0),
    lowStock: filteredItems.filter(
      (item) => item.availableQty < item.safetyStockQty && item.availableQty > 0
    ).length,
    outOfStock: filteredItems.filter((item) => item.availableQty === 0).length,
  }

  const getStatusInfo = (item: { availableQty: number; safetyStockQty: number }) => {
    if (item.availableQty === 0) {
      return { label: '零库存', class: 'bg-red-100 text-red-600', dot: 'bg-red-500' }
    }
    if (item.availableQty < item.safetyStockQty) {
      return { label: '库存偏低', class: 'bg-amber-100 text-amber-600', dot: 'bg-amber-500' }
    }
    return { label: '正常', class: 'bg-green-100 text-green-600', dot: 'bg-green-500' }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <Loading text="加载库存数据..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <InlineError message={getApiErrorMessage(error)} />
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">库存总览</h1>
          <p className="mt-1 text-sm text-gray-500">按仓库查看物料可用库存、预留库存与安全库存</p>
        </div>
        <Link
          to="/supply-chain/inventory/alert"
          className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700 transition hover:bg-amber-100"
        >
          <TriangleAlert className="h-4 w-4" />
          查看库存预警
        </Link>
      </div>

      <div className="mb-6 grid grid-cols-4 gap-4">
        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
              <Package className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.totalTypes}</div>
              <div className="text-xs text-gray-500">配件种类</div>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
              <Package className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.totalStock}</div>
              <div className="text-xs text-gray-500">当前库存合计</div>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
              <Package className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-600">{stats.lowStock}</div>
              <div className="text-xs text-gray-500">低库存</div>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
              <Package className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">{stats.outOfStock}</div>
              <div className="text-xs text-gray-500">零库存</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="搜索配件名称、编码..."
              value={searchKeyword}
              onChange={(event) => setSearchKeyword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  setAppliedKeyword(searchKeyword.trim())
                  setPage(0)
                }
              }}
              className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as StatusFilter)
              setPage(0)
            }}
            className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">全部状态</option>
            <option value="ok">正常</option>
            <option value="low">低库存</option>
            <option value="out">零库存</option>
          </select>
          <button
            onClick={() => {
              setSearchKeyword('')
              setAppliedKeyword('')
              setStatusFilter('all')
              setPage(0)
            }}
            className="flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-200"
          >
            <Filter className="h-4 w-4" />
            重置
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
        {filteredItems.length ? (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-left text-sm text-gray-500">
                <th className="px-5 py-4 font-medium">配件信息</th>
                <th className="px-5 py-4 font-medium">编码</th>
                <th className="px-5 py-4 font-medium">仓库</th>
                <th className="px-5 py-4 text-center font-medium">现有库存</th>
                <th className="px-5 py-4 text-center font-medium">已锁定</th>
                <th className="px-5 py-4 text-center font-medium">可用</th>
                <th className="px-5 py-4 text-center font-medium">安全库存</th>
                <th className="px-5 py-4 font-medium">状态</th>
                <th className="px-5 py-4 font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {filteredItems.map((item) => {
                const status = getStatusInfo(item)
                return (
                  <tr key={item.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`h-3 w-3 rounded-full ${status.dot}`} />
                        <div>
                          <div className="font-medium text-gray-900">{item.partName}</div>
                          <div className="text-xs text-gray-400">{item.specification || '-'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 font-mono text-xs text-gray-600">{item.skuCode}</td>
                    <td className="px-5 py-4 text-gray-600">{item.warehouseCode}</td>
                    <td className="px-5 py-4 text-center font-medium text-gray-900">{item.onHandQty}</td>
                    <td className="px-5 py-4 text-center text-amber-600">{item.reservedQty}</td>
                    <td className="px-5 py-4 text-center font-medium text-gray-900">{item.availableQty}</td>
                    <td className="px-5 py-4 text-center text-gray-600">{item.safetyStockQty}</td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${status.class}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <Link
                        to={`/supply-chain/inventory/ledger/${item.skuCode}`}
                        className="text-primary-600 hover:underline"
                      >
                        台账
                      </Link>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        ) : (
          <EmptyData description={appliedKeyword || statusFilter !== 'all' ? '没有找到符合条件的库存' : '暂无库存数据'} />
        )}

        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
          <div className="text-sm text-gray-500">
            显示 {filteredItems.length} 条，共 {data?.totalElements || 0} 条记录
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              disabled={page === 0}
              className="rounded border border-gray-200 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-40"
            >
              上一页
            </button>
            <span className="text-sm text-gray-600">
              第 {page + 1} / {data?.totalPages || 1} 页
            </span>
            <button
              onClick={() => setPage((current) => Math.min(current + 1, (data?.totalPages || 1) - 1))}
              disabled={page >= (data?.totalPages || 1) - 1}
              className="rounded border border-gray-200 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
